/**
 * 8TrackFit - product rail carousel
 *
 * Native scroll-snap does the moving; this only drives pagination, the dots
 * and autoplay.
 *
 * MOTION FIX 2026-09-17
 * Paging used to scroll to `page * trackWidth`. The track is
 * scroll-snap-type: x mandatory, and a whole viewport width almost never
 * lands exactly on a card boundary - so every autoplay step glided to an
 * off-snap position and the browser then YANKED it to the nearest card. That
 * snap correction at the end of each step is the jerk you see.
 * Targets are now taken from the cards themselves (the offset of the first
 * card of the page, measured against the first card in the track), so each
 * glide finishes exactly on a snap point and the browser has nothing left to
 * correct. Page count comes from cards-per-view for the same reason.
 */

(() => {
  const EPS = 2;

  class Rail {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-tf-rail-track]');
      this.dotsWrap = root.querySelector('[data-tf-rail-dots]');
      if (!this.track) return;

      this.delay = Number(root.dataset.autoplay || 0) * 1000;
      this.timer = null;
      this.page = 0;
      this.pages = 1;
      this.perView = 1;
      this.userHeld = false;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayAllowed = this.delay > 0 && !reduce;
      this.smooth = reduce ? 'auto' : 'smooth';

      this.onScroll = this.debounce(() => this.syncFromScroll(), 90);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      ['pointerenter', 'focusin', 'pointerdown', 'touchstart'].forEach((e) =>
        root.addEventListener(e, () => this.hold(), { passive: true })
      );
      ['pointerleave', 'focusout', 'touchend', 'pointerup'].forEach((e) =>
        root.addEventListener(e, () => this.release(), { passive: true })
      );

      this.resize = this.debounce(() => this.measure(), 150);
      window.addEventListener('resize', this.resize);

      this.measure();
      this.start();
    }

    debounce(fn, ms) {
      let t;
      return (...a) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...a), ms);
      };
    }

    cards() {
      return Array.prototype.filter.call(this.track.children, (el) => el.nodeType === 1);
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* distance from the start of the track to this card's snap position */
    startOf(cards, i) {
      if (!cards.length) return 0;
      const idx = Math.max(0, Math.min(i, cards.length - 1));
      return cards[idx].offsetLeft - cards[0].offsetLeft;
    }

    measure() {
      const view = this.track.clientWidth;
      if (!view) return;
      const cards = this.cards();
      if (!cards.length) return;

      /* card pitch = card width + gap, taken from two real cards so it stays
         correct at every breakpoint without hard-coding anything */
      const pitch =
        cards.length > 1
          ? cards[1].offsetLeft - cards[0].offsetLeft
          : cards[0].getBoundingClientRect().width;

      this.perView = pitch > 0 ? Math.max(1, Math.round(view / pitch)) : 1;
      this.pages = Math.max(1, Math.ceil(cards.length / this.perView));

      /* drop trailing pages that cannot scroll any further than the one
         before them - they would be dots that go nowhere */
      while (
        this.pages > 1 &&
        this.startOf(cards, (this.pages - 2) * this.perView) >= this.maxScroll() - EPS
      ) {
        this.pages -= 1;
      }

      this.renderDots();
      this.syncFromScroll();
    }

    renderDots() {
      if (!this.dotsWrap) return;
      if (this.pages < 2) {
        this.dotsWrap.hidden = true;
        this.dotsWrap.innerHTML = '';
        return;
      }
      this.dotsWrap.hidden = false;
      if (this.dotsWrap.children.length === this.pages) return;

      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < this.pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tf-rail__dot';
        b.setAttribute('aria-label', `Go to page ${i + 1}`);
        b.setAttribute('aria-current', i === this.page ? 'true' : 'false');
        b.addEventListener('click', () => {
          this.goTo(i);
          this.restart();
        });
        this.dotsWrap.appendChild(b);
      }
    }

    syncFromScroll() {
      const cards = this.cards();
      if (!cards.length) return;
      const max = this.maxScroll();

      if (this.track.scrollLeft >= max - EPS) {
        this.page = this.pages - 1;
        this.paintDots();
        return;
      }

      /* nearest card to where we actually are, then which page owns it */
      let nearest = 0;
      let best = Infinity;
      for (let i = 0; i < cards.length; i++) {
        const d = Math.abs(this.startOf(cards, i) - this.track.scrollLeft);
        if (d < best) { best = d; nearest = i; }
      }
      this.page = Math.min(this.pages - 1, Math.floor(nearest / this.perView));
      this.paintDots();
    }

    paintDots() {
      if (!this.dotsWrap) return;
      Array.from(this.dotsWrap.children).forEach((d, i) =>
        d.setAttribute('aria-current', i === this.page ? 'true' : 'false')
      );
    }

    goTo(page) {
      const cards = this.cards();
      if (!cards.length) return;
      const wanted = Math.max(0, Math.min(page, this.pages - 1));
      /* land ON a card: no post-glide snap correction, so no jerk */
      const target = Math.min(this.startOf(cards, wanted * this.perView), this.maxScroll());
      this.track.scrollTo({ left: target, behavior: this.smooth });
      this.page = wanted;
      this.paintDots();
    }

    next() {
      if (this.track.scrollLeft >= this.maxScroll() - EPS) this.goTo(0);
      else this.goTo(this.page + 1);
    }

    start() {
      if (!this.autoplayAllowed || this.userHeld) return;
      this.stop();
      this.timer = setInterval(() => {
        if (document.hidden) return;
        this.next();
      }, this.delay);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    hold() {
      this.userHeld = true;
      this.stop();
    }

    release() {
      this.userHeld = false;
      this.start();
    }
  }

  function boot() {
    document.querySelectorAll('[data-tf-rail]').forEach((el) => {
      if (el.dataset.tfRailReady === 'true') return;
      el.dataset.tfRailReady = 'true';
      el.tfRail = new Rail(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
